import React, { useState, useEffect, useRef } from 'react';
import './MessageBar.css';
import { registerMessageHistoryUpdater } from '../utils/externalControls';
import config from '../config'; // Import the centralized config

function MessageBar() {
    const [message, setMessage] = useState('');
    const [chatHistory, setChatHistory] = useState([]);
    const [showChat, setShowChat] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const chatRef = useRef(null);

    // Register with external controls
    useEffect(() => {
        registerMessageHistoryUpdater((newMessage) => {
            setChatHistory(prev => [...prev, newMessage]);
        });
    }, []);

    // Scroll to bottom of chat when new messages arrive
    useEffect(() => {
        if (chatRef.current && showChat) {
            chatRef.current.scrollTop = chatRef.current.scrollHeight;
        }
    }, [chatHistory, showChat]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (message.trim() === '') return;

        // Add user message to chat history
        const userMessage = {
            content: message,
            isUser: true,
            timestamp: new Date().toISOString()
        };
        setChatHistory(prev => [...prev, userMessage]);

        // Set loading state
        setIsLoading(true);

        // Update AI tile with loading animation
        /* Commented out AI Tile loading animation
        if (window.portfolioControls && typeof window.portfolioControls.updateAiResponse === 'function') {
            window.portfolioControls.updateAiResponse(
                '<div class="loading-dots"><span>.</span><span>.</span><span>.</span></div>'
            );
        }
        */

        try {
            // Format the message history for the API
            const historyForApi = chatHistory.map(msg => ({
                role: msg.isUser ? "user" : "assistant",
                content: msg.content
            }));

            // Call the Flask API using the configuration
            const response = await fetch(config.CHAT_ENDPOINT, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                    'Pragma': 'no-cache',
                    'Expires': '0'
                },
                cache: 'no-store',
                body: JSON.stringify({
                    message: message,
                    history: historyForApi
                })
            });

            const data = await response.json();

            if (data.success) {
                // Add AI response to chat history
                const aiResponse = {
                    content: data.response,
                    isUser: false,
                    timestamp: new Date().toISOString()
                };
                setChatHistory(prev => [...prev, aiResponse]);

                // Update the AI tile if the external control is available
                if (window.portfolioControls && typeof window.portfolioControls.updateAiResponse === 'function') {
                    window.portfolioControls.updateAiResponse(data.response);
                    
                    // Ensure scrolling to the AI Tile happens even if there's a delay
                    setTimeout(() => {
                        if (window.portfolioControls && typeof window.portfolioControls.scrollToAiTile === 'function') {
                            window.portfolioControls.scrollToAiTile();
                        }
                    }, 200);
                }
            } else {
                // Handle error response
                const errorMsg = {
                    content: `Error: ${data.error || 'Failed to get response'}`,
                    isUser: false,
                    timestamp: new Date().toISOString()
                };
                setChatHistory(prev => [...prev, errorMsg]);

                // Display error in AI tile
                if (window.portfolioControls && typeof window.portfolioControls.updateAiResponse === 'function') {
                    window.portfolioControls.updateAiResponse(`Error: ${data.error || 'Failed to get response'}`);
                    
                    // Also ensure scrolling on error messages
                    setTimeout(() => {
                        if (window.portfolioControls && typeof window.portfolioControls.scrollToAiTile === 'function') {
                            window.portfolioControls.scrollToAiTile();
                        }
                    }, 200);
                }
            }
        } catch (error) {
            console.error('Error calling API:', error);
            // Add error message to chat history
            const errorMsg = {
                content: 'Sorry, there was a problem connecting to the AI service. Please check that the backend server is running.',
                isUser: false,
                timestamp: new Date().toISOString()
            };
            setChatHistory(prev => [...prev, errorMsg]);

            // Display error in AI tile
            if (window.portfolioControls && typeof window.portfolioControls.updateAiResponse === 'function') {
                window.portfolioControls.updateAiResponse('Sorry, there was a problem connecting to the AI service. Please check that the backend server is running.');
            }
        } finally {
            setIsLoading(false);
        }

        // Clear the input after sending
        setMessage('');

        // Do not open chat history automatically
        // setShowChat(true);
    };

    const toggleChat = () => {
        setShowChat(prev => !prev);
    };

    return (
        <>
            {showChat && (
                <div className="chat-history" ref={chatRef}>
                    {chatHistory.map((msg, index) => (
                        <div key={index} className={`message ${msg.isUser ? 'user' : 'system'}`}>
                            <div className="message-content">{msg.content}</div>
                            <div className="message-timestamp">
                                {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <div className="message-bar">
                {chatHistory.length > 0 && (
                    <button
                        className="toggle-chat-button"
                        onClick={toggleChat}
                        aria-label={showChat ? "Hide chat history" : "Show chat history"}
                    >
                        {showChat ? "▼" : "▲"}
                    </button>
                )}
                <form onSubmit={handleSubmit} className="message-form">
                    <input
                        type="text"
                        className="message-input"
                        placeholder="Type a message to the AI assistant..."
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        id="message-input"
                    />
                    <button type="submit" className="send-button" disabled={!message.trim() || isLoading} id="send-message-button">
                        {isLoading ? (
                            <div className="button-loading-dots">
                                <span>.</span><span>.</span><span>.</span>
                            </div>
                        ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="22" y1="2" x2="11" y2="13"></line>
                                <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                            </svg>
                        )}
                    </button>
                </form>
            </div>
        </>
    );
}

export default MessageBar;
